import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useMutation } from '@tanstack/react-query';
import { ServiceRotationFormType, TowRotationProps } from './types';
import { RotationResource, RotationActionRequest, ServiceRotationModal } from '@mark43/cad-api';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useToast } from '@cad-arc';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { UseFormReturn } from 'react-hook-form';
import { addMultiTowVehiclesToEventForm } from './helpers';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';

export function useServiceRotationDrawerMutation({
    form,
    eventId,
    towRotationProps,
    updateForm,
    selectedMultiVehiclesIndexes,
    selectedMergedMultiVehiclesIndexes,
}: {
    form: UseFormReturn<ServiceRotationFormType>;
    eventId: number;
    towRotationProps?: TowRotationProps;
    updateForm: (data: ServiceRotationModal) => void;
    selectedMultiVehiclesIndexes: number[];
    selectedMergedMultiVehiclesIndexes: number[];
}) {
    const apiClient = useApiClient();
    const drawerControls = useDrawerControls();
    const toast = useToast();
    const { enhancedServiceRotation: isEnhancedServiceRotationEnabled } = useEnabledFeatures();
    const eventPanelConfiguration = useBootstrapState().eventPanelConfiguration;

    const mutation = useMutation(
        (rotationActionRequest: RotationActionRequest) => {
            const { serviceProvider } = rotationActionRequest;
            return apiClient
                .request<RotationResource.TakeAction>({
                    method: 'POST',
                    url: `rotation/${eventId}/action`,
                    data: rotationActionRequest,
                })
                .then((resp) => {
                    if (towRotationProps && serviceProvider) {
                        const { towVehicleBaseKey, towVehicleIndex, eventForm } = towRotationProps;
                        eventForm.setValue(
                            `${towVehicleBaseKey}.${towVehicleIndex}.towCompanyOption`,
                            {
                                value: serviceProvider.attributeId,
                                label: serviceProvider.label,
                                isOther: false,
                            },
                            {
                                shouldDirty: true,
                                shouldValidate: true,
                            }
                        );
                        eventForm.setValue(
                            `${towVehicleBaseKey}.${towVehicleIndex}.towedToLocation`,
                            serviceProvider.consolidatedLocationView ?? null,
                            {
                                shouldDirty: true,
                                shouldValidate: true,
                            }
                        );
                        eventForm.setValue(
                            `${towVehicleBaseKey}.${towVehicleIndex}.rotationServiceListPositionId`,
                            resp.data.data?.rotationServiceListPositionId ?? null,
                            {
                                shouldDirty: true,
                                shouldValidate: true,
                            }
                        );

                        if (
                            isEnhancedServiceRotationEnabled &&
                            rotationActionRequest.rotationAction === 'ACCEPT'
                        ) {
                            addMultiTowVehiclesToEventForm({
                                eventForm,
                                selectedMultiVehiclesIndexes,
                                selectedMergedMultiVehiclesIndexes,
                                serviceRotationModalResponse: resp.data,
                                serviceProvider,
                                eventPanelConfiguration,
                            });
                        }
                    }

                    if (rotationActionRequest.rotationAction === 'ACCEPT') {
                        drawerControls.close();
                    } else {
                        if (resp.data.data) {
                            updateForm(resp.data.data);
                        }
                        // using reset instead of setValues for TS performance
                        form.reset({ ...form.getValues(), reason: '' });
                    }
                });
        },
        {
            onError: createApiErrorHandler(() => {
                toast({
                    status: 'error',
                    description: errorOccurredWhile('submitting'),
                });
            }),
        }
    );
    return mutation;
}
